import React, { useState, useRef } from 'react'
import { X, Upload, FileText, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react'
import { upload } from '../../services/api'
import { compressImage, formatFileSize } from '../../utils/imageCompression'
import { toast } from 'react-hot-toast'

const ReceiptUploadModal = ({ isOpen, onClose, cuotaId, monto, saldoPendiente, onSuccess, urlPago, estudianteId=null }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [originalSize, setOriginalSize] = useState(0)
  const [metodo, setMetodo] = useState('TRANSFERENCIA')
  const [montoIngresado, setMontoIngresado] = useState(monto?.toString() || '')
  const fileInputRef = useRef(null)

  const methodOptions = [
    { value: 'TRANSFERENCIA', label: 'Transferencia Bancaria' },
    { value: 'EFECTIVO', label: 'Efectivo' }
  ]

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos JPG, PNG o PDF')
      return
    }

    // Validar tamaño (máximo 5MB según backend)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('El archivo es muy grande. Máximo 5MB')
      return
    }

    setOriginalSize(file.size)

    // Mostrar preview si es imagen
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreview(e.target.result)
      }
      reader.readAsDataURL(file)

      // Comprimir imagen
      try {
        setUploading(true)
        const compressed = await compressImage(file, 1920, 1920, 0.8, 2)
        setSelectedFile(compressed)
        toast.success(`Imagen comprimida: ${formatFileSize(originalSize)} → ${formatFileSize(compressed.size)}`)
      } catch (error) {
        console.error('Error comprimiendo imagen:', error)
        setSelectedFile(file)
      } finally {
        setUploading(false)
      }
    } else {
      setPreview(null)
      setSelectedFile(file)
    }
  }

  const handleUpload = async () => {
    // Validación condicional: comprobante es obligatorio solo para transferencias
    if (metodo === 'TRANSFERENCIA' && !selectedFile) {
      toast.error('El comprobante es obligatorio para transferencias bancarias')
      return
    }

    // if (!selectedFile) {
    //   toast.error('Selecciona un archivo')
    //   return
    // }

    if (!metodo) {
      toast.error('Selecciona un método de pago')
      return
    }

    const montoNum = parseFloat(montoIngresado)
    if (isNaN(montoNum) || montoNum <= 0) {
      toast.error('Ingresa un monto válido')
      return
    }

    if (saldoPendiente && montoNum > saldoPendiente) {
      toast.error(`El monto no puede exceder ${saldoPendiente} Bs (saldo pendiente)`)
      return
    }

    if (!cuotaId) {
      toast.error('Error: Información de cuota no disponible')
      return
    }

    const formData = new FormData()
    formData.append('cuota_id', cuotaId)
    formData.append('monto', montoNum)
    formData.append('metodo', metodo)
    if (selectedFile) {
      formData.append('comprobante', selectedFile)
    }
    if(estudianteId) {
      formData.append('estudianteId', estudianteId)
    }

    // if (metodo !== 'TRANSFERENCIA') {
    //   return
    // }
    try {
      setUploading(true)
      console.log('Subiendo comprobante de pago:', {
        cuota_id: cuotaId,
        monto: montoNum,
        metodo: metodo,
        archivo: selectedFile?.name || 'Sin archivo',
        tamaño: selectedFile?.size || 'N/A'
      })

      const response = await upload(urlPago, formData)

      console.log('Respuesta del servidor:', response.data)

      if (response.data.success) {
        toast.success('Comprobante subido exitosamente')
        setTimeout(() => {
          onSuccess?.()
          handleClose()
        }, 500)
      } else {
        toast.error(response.data.message || 'Error al subir el comprobante')
      }
    } catch (error) {
      console.error('Error subiendo comprobante:', error)
      const errorMessage =
        error.response?.data?.message || 'Error al subir el comprobante'
      toast.error(errorMessage)
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreview(null)
    setOriginalSize(0)
    setMetodo('TRANSFERENCIA')
    setMontoIngresado(monto?.toString() || '')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Subir Comprobante de Pago
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Saldo Pendiente:</p>
                <p className="text-lg font-bold">
                  {new Intl.NumberFormat('es-BO', {
                    style: 'currency',
                    currency: 'BOB'
                  }).format(saldoPendiente || monto || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Método de Pago */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Método de pago
            </label>
            <select
              value={metodo}
              onChange={(e) => setMetodo(e.target.value)}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900
                focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {methodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Monto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Monto pagado (Bs.)
            </label>
            <input
              type="number"
              value={montoIngresado}
              onChange={(e) => setMontoIngresado(e.target.value)}
              disabled={uploading}
              placeholder="0.00"
              step="0.01"
              min="0.01"
              max={saldoPendiente || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900
                focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {saldoPendiente && (
              <p className="text-xs text-gray-500 mt-1">
                Máximo: {new Intl.NumberFormat('es-BO', {
                  style: 'currency',
                  currency: 'BOB'
                }).format(saldoPendiente)}
              </p>
            )}
          </div>

          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comprobante de pago
              {metodo === 'TRANSFERENCIA' && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              disabled={uploading}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-green-50 file:text-green-700
                hover:file:bg-green-100
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-2">
              JPG, PNG o PDF (Máximo 5MB)
            </p>
            {metodo === 'TRANSFERENCIA' && (
              <p className="text-xs text-red-600 font-medium mt-1">
                ⚠️ Obligatorio para transferencias bancarias
              </p>
            )}
          </div>

          {/* Preview */}
          {preview && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Vista previa
              </p>
              <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          )}

          {selectedFile && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="text-gray-600">
                <span className="font-medium">Archivo seleccionado:</span> {selectedFile.name}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Tamaño: {formatFileSize(selectedFile.size)}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={handleClose}
            disabled={uploading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium
              hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleUpload}
            disabled={metodo === 'TRANSFERENCIA' ? !selectedFile || uploading : uploading}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium
              hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Subiendo...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Subir
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReceiptUploadModal
